import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { environment } from '@environments/environment'
import { GlobalFunctions } from '@global/GlobalFunctions';
import { LevelAsset } from './assets-data.service';
import { UploadFile } from './files.service';
import { DataPoint } from './datapoints-data.service';
import { PanelModel } from '@syncfusion/ej2-angular-layouts';

export class OrganisationDashboard {

	/**
	 * Default Constructor 
	 * @param OrganisationDashboardID 
	 * @param OrganisationUID 
	 * @param DashboardName 
	 * @param DashboardConfig 
	 * @param CreatedBy 
	 * @param CreatedByName 
	 */
	constructor(
		public OrganisationDashboardID: number,
		public OrganisationUID: string,
		public DashboardName: string,
		public DashboardConfig: { panel: PanelModel, info: any }[],
		public CreatedBy: number,
		public CreatedByName: string,
		public DefaultDashboard: boolean,
		public UserDefault?: boolean,
		public Locked?: boolean
	) { }
}

export class DataModelNodeRequest {

	/**
	 * Default Constructor
	 * @param ParentNodeUID ParentNodeUID  
	 * @param NodeType NodeType 
	 * @param Name Name 
	 * @param Description Description 
	 * @param DatapointIDs DatapointIDs 
	 */
	constructor(
		public ParentNodeUID: string = "",
		public NodeType: string,
		public Name: string,
		public Description: string,
		public DatapointIDs: string = ""
	) { }
}

/**
 * IOrganisationDataModelNode
 */
export interface IOrganisationDataModelNode {

	/** DataModelNodeID */
	DataModelNodeID: number;

	/** DataModelNodeUID */
	DataModelNodeUID: string;

	/** OrganisationUID */
	OrganisationUID: string;

	/** NodeType */
	NodeType: string;

	/** Name */
	Name: string;

	/** Description */
	Description: string;

	/** NodeLevel */
	NodeLevel: number;

	/** ParentNodeUID */
	ParentNodeUID?: string;

	/** DatapointIDs */
	Datapoints?: DataPoint[];

	/** SubChild */
	SubChild?: any[];
}

/**
 * OrganisationDataModelNode
 */
export class OrganisationDataModelNode implements IOrganisationDataModelNode {

	/**
	 * Default Constructor
	 * @param DataModelNodeID DataModelNodeID  
	 * @param DataModelNodeUID DataModelNodeUID 
	 * @param OrganisationUID OrganisationUID 
	 * @param NodeType NodeType 
	 * @param Name Name 
	 * @param Description Description  
	 * @param NodeLevel NodeLevel 
	 * @param ParentNodeUID ParentNodeUID 
	 * @param DatapointIDs DatapointIDs 
	 * @param SubChild SubChild 
	 */
	constructor(
		public DataModelNodeID: number,
		public DataModelNodeUID: string,
		public OrganisationUID: string,
		public NodeType: string,
		public Name: string,
		public Description: string,
		public NodeLevel: number,
		public ParentNodeUID?: string,
		public Datapoints?: DataPoint[],
		public SubChild?: any[],
		public Expanded?: boolean
	) { }
}

/**
 * Organisation Structure
 */
export interface IOrganisation {
	/** Organisation ID */
	Id: number;
	/** Organisation UiD */
	Uid: string;
	/** Organisation Name */
	Name: string;
	/** Organisation Description */
	Description: string;
	/** Organisation Image */
	ImageURL: string;
	/** Organisation Expanded in structure*/
	Expanded: boolean;
	/** Organisation Functional Location*/
	FunctionalLocation: String;
	/** Last modified Epoch */
	Modified: number;
	/** RemindersEnabled */
	RemindersEnabled: boolean;
	/** ReminderInterval */
	ReminderInterval: number;
	/** Account GUID */
	AccountGUID?: string;
}

/**
 * Organisation Structure
 * implements IOrganisation
 */
export class Organisation implements IOrganisation {
	/**
	 * Organisation
	 * @param Id ID
	 * @param Uid UID
	 * @param Name Name
	 * @param Description Description
	 * @param ImageURL ImageURL
	 * @param Expanded Expanded
	 * @param FunctionalLocation Functional Location
	 * @param [Modified=0] Modified Epoch Time
	 */
	constructor(
		/** Organisation ID */
		public Id: number = 0,
		/** Organisation UiD */
		public Uid: string = "",
		/** Organisation Name */
		public Name: string = "",
		/** Organisation DEscription */
		public Description: string = "",
		/** Organisation Image */
		public ImageURL: string = "",
		/** Organisation Expanded in structure*/
		public Expanded: boolean = false,
		/** Organisation Functional Location*/
		public FunctionalLocation: String = "",
		/** Last Modified Epoch Time */
		public Modified: number = 0,
		/** RemindersEnabled */
		public RemindersEnabled: boolean = false,
		/** ReminderInterval */
		public ReminderInterval: number = 0,
		/** Account GUID */
		public AccountGUID?: string
	) { }
}

/**
 * Organisation Structure
 */
export interface IOrganisationsHierarchy {
	/** subLocationID */
	Id: number;
	/** Hierarchical Item ID */
	Uid: string;
	/** Parent UID */
	ParentUid: string;
	/** Hierarchical Item Name */
	Name: string;
	/** Hierarchical Item  Description */
	Description: string;
	/** Hierarchical Item Image */
	ImageURL: string;
	/** Hierarchical Expanded in structure*/
	Expanded: boolean;
	/** Location Type */
	LocationType: string;
	/** Hierarchical Item Assets */
	Assets: LevelAsset[];
	/** Hierarchical Item Children items */
	SubChild: OrganisationsHierarchy[];
	/** Number of Assets */
	TotalAssetCount: number;
	/** Number of WatchList Items */
	TotalWatchCount: number;
	/** Number of Actioned Items */
	TotalActionCount: number;
	/** Level */
	Level: number;
	/** Functional Location */
	FunctionalLocation: string;
	/** Hidden */
	Hidden: boolean;
	/** Last Modified Epoch Time */
	Modified: number;
	/** Security */
	Security: string;
	/** ParentFunctionalLocation */
	ParentFunctionalLocation: string;
	/** ParentOrder */
	ParentOrder: number;

}

/**
 * OrganisationsHierarchy Class
 * implements IOrganisationsHierarchy
 */
export class OrganisationsHierarchy implements IOrganisationsHierarchy {
	/**
	 * OrganisationsHierarchy
	 * @param Id
	 * @param Uid Uid
	 * @param ParentUid ParentUid
	 * @param Name Name
	 * @param Description Description
	 * @param ImageURL ImageURL
	 * @param Expanded Expanded
	 * @param LocationType LocationType
	 * @param Assets Assets
	 * @param SubChild SubChild
	 * @param TotalAssetCount TotalAssetCount
	 * @param TotalWatchCount TotalWatchCount
	 * @param TotalActionCount TotalActionCount
	 * @param FunctionalLocation Functional Location
	 * @param Hidden Hidden
	 * @param Level Level
	 * @param Modified Last Modified Epoch Time
	 * @param Security Security
	 * @param ParentFunctionalLocation ParentFunctionalLocation
	 * @param ParentOrder ParentOrder
	 */
	constructor(
		public Id: number = 0,
		/** Hierarchical Item ID */
		public Uid: string = "",
		/** Parent UID */
		public ParentUid: string = "",
		/** Hierarchical Item Name */
		public Name: string = "",
		/** Hierarchical Item  Description */
		public Description: string = "",
		/** Hierarchical Item Image */
		public ImageURL: string = "",
		/** Hierarchical Expanded in structure*/
		public Expanded: boolean = false,
		/** Location Type */
		public LocationType: string = "",
		/** Hierarchical Item Assets */
		public Assets: LevelAsset[] = [],
		/** Hierarchical Item Children items */
		public SubChild: OrganisationsHierarchy[] = [],
		/** Hierarchical Item Children items */
		public TotalAssetCount: number = 0,
		/** Number of WatchList Items */
		public TotalWatchCount: number = 0,
		/** Number of Actioned Items */
		public TotalActionCount: number = 0,
		/** Level */
		public Level: number = 0,
		/** Organisation Functional Location*/
		public FunctionalLocation: string = "",
		/** Hidden */
		public Hidden: boolean = false,
		/** Last Modified Epoch Time */
		public Modified: number = 0,
		/** Security */
		public Security: string = "",
		/** ParentFunctionalLocation */
		public ParentFunctionalLocation: string = "",
		/** ParentOrder */
		public ParentOrder: number = 0

	) { }
}

/**
 * ILocationGroup
 */
export interface ILocationGroup {
	/** Account Group ID */
	AccountGroupID: number;
	/** Hierarchical Item ID */
	Name: string;
	/** Inherited */
	Inherited: boolean;
	/** Permissions */
	Permissions: ILocationGroupPermissions[]
}

/**
 * ILocationGroupPermissions
 */
export interface ILocationGroupPermissions {
	/** View */
	View: boolean;
	/** Write */
	Write: boolean;
	/** Inherited */
	Inherited: boolean;
}

/**
 * Find Parent Nodes in Data
 * @param data
 * @param nodes Structure
 * @param targetUID ID To Find
 * @param ancestors List of Ancesters
 * @returns List of Ancesters
 */
const findAncestors = (
	data: IOrganisationsHierarchy,
	nodes: OrganisationsHierarchy,
	targetUID: string,
	ancestors: OrganisationsHierarchy[] = []): OrganisationsHierarchy[] => {

	if (nodes.Uid.toUpperCase() === targetUID.toUpperCase()) {
		// If the current child has the target SubChild ID, add the ancestor IDs to the array
		//ancestors.unshift(nodes);
	} else {
		if (nodes.SubChild) {
			for (const root of nodes.SubChild) {

				if (root.Uid.toUpperCase() == targetUID.toUpperCase()) {
					// If the current child has the target SubChild ID, add the ancestor IDs to the array
					ancestors.unshift(nodes);
					// Recursively find ancestors for the parent node
					findAncestors(data, data, nodes.Uid, ancestors);
				}
				else {
					if (root.SubChild) {
						for (const child of root.SubChild) {
							if (child.Uid.toUpperCase() === targetUID.toUpperCase()) {
								// If the current child has the target SubChild ID, add the ancestor IDs to the array
								ancestors.unshift(root);
								// Recursively find ancestors for the parent node
								findAncestors(data, data, root.Uid, ancestors);
							} else {
								// Recursively search in the child nodes
								findAncestors(data, child, targetUID, ancestors);
							}
						}
					}
				}
			}
		}
	}
	return ancestors;
};

/**
 * Find Parent IDs
 * @param nodes
 * @param targetSubChildID
 * @param parentIDs
 * @returns
 */
function findParentIDs(nodes: IOrganisationsHierarchy[], targetSubChildID: string, parentIDs: string[] = []): string[] {
	for (const root of nodes) {
		if (root.SubChild) {
			for (const child of root.SubChild) {
				if (child.Uid === targetSubChildID) {
					// If the current child has the target SubChild ID, add the parent ID to the array
					parentIDs.push(root.Uid);
				}
				// Recursively search in the child nodes
				findParentIDs(child.SubChild || [], targetSubChildID, parentIDs);
			}
		}
	}
	return parentIDs;
}

/**
 * Default Class
 */
@Injectable({
	providedIn: 'root'
})
export class OrganisationDataService {

	/** API Route */
	apiOrganisation: string = "api/{0}/Organisations"
	/** apiOrganisationsHierarchy */
	apiOrganisationsHierarchy: string = "api/{0}/OrganisationsHierarchy"
	/** apiHierarchyPermissions */
	apiHierarchyPermissions: string = "api/{0}/HierarchyPermissions"

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions Global Functions
	 */
	constructor(
		private http: HttpClient,
		public globalFunctions: GlobalFunctions
	) { }

	/**
	 * Get a list of Organisations from API
	 * @param AccountGUID AccountGUID
	 * @param orgID Optional
	 * @returns Observable<any>
	 */
	getOrganisations$(AccountGUID: string, orgID: string = ""): Observable<any> {
		let GetData = {}

		let url = this.apiOrganisation;
		url = url.replace("{0}", AccountGUID);

		if (orgID != "") {
			url += "/" + orgID
		}

		url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", GetData);
	}

	/**
	 * Get a list of Organisations from API
	 * @param AccountGUID AccountGUID
	 * @param orgID Optional
	 * @returns Observable<any>
	 */
	patchOrganisation$(AccountGUID: string, orgID: string, organisation: IOrganisation, uploadFile?: UploadFile): Observable<any> {

		let organisationData = JSON.stringify(organisation);

		let url = this.apiOrganisation;
		url = url.replace("{0}", AccountGUID);
		url = `${environment.apiUrl}/${url}/${orgID}`;

		// Create form data
		const formData = new FormData();
		// Set the Organisation JSON Data in the Form
		formData.append("organisationData", organisationData)

		if (uploadFile) {
			if (uploadFile.fileBlob) {
				// Set the Storage Blob Container (folder in Blob Containers)
				formData.append("container", uploadFile.container)
				// Store form name as "file" with file data
				formData.append("file", uploadFile.fileBlob, uploadFile.filename);
				// Send filename for completeness
				formData.append("filename", uploadFile.filename);
				// State folder location for blob storage
				formData.append("folderlocation", uploadFile.folderLocation)
			}
		}
		// Make http post request over api
		// with formData as req
		// return this.http.patch(url, formData)
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", organisationData);
	}

	/**
	 * Post a new organisation
	 * @param AccountGUID AccountGUID
	 * @param orgID Optional
	 * @returns Observable<any>
	 */
	postOrganisation$(AccountGUID: string, organisation: IOrganisation): Observable<any> {

		let data = JSON.stringify(organisation);

		let url = this.apiOrganisation;
		url = url.replace("{0}", AccountGUID);
		url = `${environment.apiUrl}/${url}`;
		// Make http post request over api
		// with formData as req
		// return this.http.patch(url, formData)
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Delete an Organisation Location
	 * @param AccountGUID
	 * @param orgUID
	 * @param location
	 * @returns
	 */
	deleteOrganisation$(AccountGUID: string, Organisation: IOrganisation): Observable<any> {

		let data = {};

		let url = this.apiOrganisation;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${Organisation.Uid}`;
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * Get a list of Organisations from API
	 * @param AccountGUID AccountGUID
	 * @param orgUID
	 * @returns Observable<any>
	 */
	getOrganisationStructure$(AccountGUID: string, orgUID: string, showHidden: number = 0): Observable<any> {

		const data = {}
		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/locations?showHidden=${showHidden}`;
		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 *
	 * @param AccountGUID
	 * @param orgUID
	 * @param subLocation
	 * @param newSubLocation
	 */
	postOrganisationLocation$(AccountGUID: string, orgUID: string, subLocation: IOrganisationsHierarchy, newSubLocation: IOrganisationsHierarchy): Observable<any> {

		let data = JSON.stringify(newSubLocation);
		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/locations/${subLocation.Uid}`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Patch Organisation Location Details
	 * @param AccountGUID
	 * @param orgUID
	 * @param location
	 * @returns
	 */
	patchOrganisationLocation$(AccountGUID: string, orgUID: string, location: IOrganisationsHierarchy): Observable<any> {

		let data = JSON.stringify(location);

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/locations/${location.Uid}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * Delete an Organisation Location
	 * @param AccountGUID
	 * @param orgUID
	 * @param location
	 * @returns
	 */
	deleteOrganisationLocation$(AccountGUID: string, orgUID: string, location: IOrganisationsHierarchy): Observable<any> {

		let data = {};

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/locations/${location.Uid}`;
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * Patch Organisation Permissions
	 * @param AccountGUID
	 * @param orgUID
	 * @param locationUID
	 * @param permissions
	 * @returns
	 */
	postOrganisationPermissions$(AccountGUID: string, orgUID: string, locationUID: string, permissions: ILocationGroup): Observable<any> {

		let data = JSON.stringify(permissions);

		let url = this.apiHierarchyPermissions;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/locations/${locationUID}/permissions`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Patch Organisation Permissions
	 * @param AccountGUID
	 * @param orgUID
	 * @param locationUID
	 * @param permissions
	 * @returns
	 */
	patchOrganisationPermissions$(AccountGUID: string, orgUID: string, locationUID: string, permissions: ILocationGroup): Observable<any> {

		let data = JSON.stringify(permissions);

		let url = this.apiHierarchyPermissions;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/locations/${locationUID}/permissions`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * Patch Organisation Permissions
	 * @param AccountGUID
	 * @param orgUID
	 * @param location
	 * @param accountGroupID
	 * @returns
	 */
	deleteOrganisationPermission$(AccountGUID: string, orgUID: string, locationUID: string, accountGroupID: number): Observable<any> {

		let data = {};

		let url = this.apiHierarchyPermissions;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/locations/${locationUID}/permissions/${accountGroupID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}


	/**
	 * Find a folder in the Organisation Structure
	 * @param uid
	 * @param data
	 * @returns
	 */
	findObjectByUID(uid: string, data: IOrganisationsHierarchy[]): any | undefined {

		for (const item of data) {
			/** Found its self */
			if (typeof (item.Uid) == "string") {
				if (item.Uid.toUpperCase() === uid.toUpperCase()) {
					return item;
				};
			} else {
				if (item.Uid == uid) {
					return item;
				};
			}

			/** Check the sub items */
			if (item.SubChild) {
				const found = this.findObjectByUID(uid, item.SubChild);
				if (found) {
					return found;
				};
			};
		};
		return undefined;
	}


	/**
	 * Get Hierarchical Objects
	 * @param root
	 * @param subChildUID
	 * @param parentIDs
	 * @returns
	 */
	getParentNames(root: IOrganisationsHierarchy, subChildUID: string, parentIDs: string[] = []) {
		try {
			return findAncestors(root, root, subChildUID);
		}
		catch (error) {
			return []
		}
	}

	// /**
	//  * Get all parent names for rwquested folder
	//  * @param foundItem
	//  * @param hierarchicalData
	//  * @returns
	//  */
	// getParentNames(foundItem: any, hierarchicalData: any): string[] {
	//     const parentNames: string[] = [];
	//     let currentItem = foundItem;
	//     while (currentItem) {
	//         parentNames.unshift(currentItem['name']);
	//         //currentItem = this.findObjectById(currentItem['id'].substring(0, currentItem['id'].lastIndexOf('-')), hierarchicalData);
	//         currentItem = this.findObjectById(currentItem['id'], hierarchicalData);
	//     }

	//     return parentNames; //.join(' > ');
	// }

	/**
	 * Get Parent Objects
	 * @param root
	 * @param subChildUID
	 * @returns
	 */
	getParents(root: IOrganisationsHierarchy, subChildUID: string) {
		try {
			if (root.Uid.toUpperCase() === subChildUID.toUpperCase()) {
				return [root]
			}
			else {
				return findAncestors(root, root, subChildUID);
			}
		}
		catch (error) { return [] }
	}

	/**
	 * Get child folders for selected Location
	 * @param foundItem
	 * @returns
	 */
	getChildren(foundItem: any): IOrganisationsHierarchy[] {

		let children: IOrganisationsHierarchy[] = [];

		let currentItem = foundItem;

		children = currentItem.SubChild

		return children; //.join(' > ');
	}

	/**
	 * getOrganisationEnergyData
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID  
	 * @param from from 
	 * @param to to 
	 * @returns 
	 */
	getOrganisationEnergyData$(AccountGUID: string, orgUID: string, from: number, to: number, datapointIDs: string = ""): Observable<any> {

		const data = {}
		let url = this.apiOrganisation;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/energyData?from=${from}&to=${to}`;

		if (datapointIDs != "") {
			url += `&datapointIDs=${datapointIDs}`;
		}

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * getOrganisationEnergyStructure
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param showHidden showHidden 
	 * @param targetSI targetSI 
	 * @returns 
	 */
	getOrganisationEnergyStructure$(AccountGUID: string, orgUID: string, showHidden: number = 0, targetSI: number = 15): Observable<any> {
		const data = {}
		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/energyLocations?showHidden=${showHidden}&targetSI=${targetSI}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * getOrganisationDataModel
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @returns 
	 */
	getOrganisationDataModel$(AccountGUID: string, orgUID: string): Observable<any> {
		const data = {}
		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dataModel`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * postOrganisationDataModeNode
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param nodeData nodeData 
	 * @returns 
	 */
	postOrganisationDataModeNode$(AccountGUID: string, orgUID: string, nodeData: DataModelNodeRequest): Observable<any> {
		let data = JSON.stringify(nodeData);

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dataModel`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * deleteOrganisationDataModelNode
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param nodeUID nodeUID 
	 * @returns 
	 */
	deleteOrganisationDataModelNode$(AccountGUID: string, orgUID: string, nodeUID: string): Observable<any> {
		let data = {};

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dataModel/${nodeUID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * patchOrganisationDataModelNode
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param nodeUID nodeUID 
	 * @param node node 
	 * @returns 
	 */
	patchOrganisationDataModelNode$(AccountGUID: string, orgUID: string, nodeUID: string, node: OrganisationDataModelNode): Observable<any> {
		let data = JSON.stringify(node);

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dataModel/${nodeUID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * postOrganisationDashboard
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param dashboardName dashboardName 
	 * @param dashboardConfig dashboardConfig 
	 * @returns 
	 */
	postOrganisationDashboard$(AccountGUID: string, orgUID: string, dashboardName: string, dashboardConfig: string, defaultDashboard: boolean, utilityDashboard: boolean): Observable<any> {
		let data = JSON.stringify({
			DashboardName: dashboardName,
			DashboardConfig: dashboardConfig,
			DefaultDashboard: defaultDashboard,
			UtilityDashboard: utilityDashboard
		});

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dashboard`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * getOrganisationDashboard
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param dashboardID dashboardID 
	 * @returns 
	 */
	getOrganisationDashboard$(AccountGUID: string, orgUID: string, dashboardID?: number): Observable<any> {
		const data = {}
		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dashboard`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	 * patchtOrganisationDashboard
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param dashboardID dashboardID 
	 * @param dashboardName dashboardName 
	 * @param dashboardConfig dashboardConfig 
	 * @returns 
	 */
	patchOrganisationDashboard$(AccountGUID: string, orgUID: string, dashboardID: number, dashboardName: string, dashboardConfig: string, defaultDashboard: boolean): Observable<any> {
		let data = JSON.stringify({
			DashboardName: dashboardName,
			DashboardConfig: dashboardConfig,
			DefaultDashboard: defaultDashboard
		});

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dashboard/${dashboardID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * deleteOrganisationDashboard
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param dashboardID dashboardID 
	 * @returns 
	 */
	deleteOrganisationDashboard$(AccountGUID: string, orgUID: string, dashboardID: number): Observable<any> {
		let data = {};

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dashboard/${dashboardID}`;
		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * postUserDefaultDashboard
	 * @param AccountGUID AccountGUID 
	 * @param orgUID orgUID 
	 * @param dashboardID dashboardID 
	 * @returns 
	 */
	postUserDefaultDashboard$(AccountGUID: string, orgUID: string, dashboardID: number): Observable<any> {
		let data = {};

		let url = this.apiOrganisationsHierarchy;
		url = url.replace("{0}", AccountGUID);

		url = `${environment.apiUrl}/${url}/${orgUID}/dashboard/${dashboardID}/userDefault`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

}
